import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { intelligentFeatures } from "@/mocks/home";

gsap.registerPlugin(ScrollTrigger);

export default function IntelligentSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const list = listRef.current;
    const image = imageRef.current;
    if (!section || !title || !list || !image) return;

    const titleLines = title.querySelectorAll("[data-line]");
    const listItems = list.querySelectorAll("[data-item]");

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 75%",
        toggleActions: "play none none none",
      },
    });

    tl.fromTo(
      titleLines,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, stagger: 0.08, ease: "power3.out" }
    );

    tl.fromTo(
      listItems,
      { x: -30, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: "power3.out" },
      "-=0.4"
    );

    const imageTween = gsap.fromTo(
      image,
      { x: 60, opacity: 0, scale: 1.05 },
      {
        x: 0,
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: image,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
      imageTween.scrollTrigger?.kill();
      imageTween.kill();
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-background-100 py-20 md:py-28 lg:py-36 px-6 md:px-10 lg:px-14 overflow-x-clip">
      <div className="max-w-7xl mx-auto">
        {/* Headline */}
        <div ref={titleRef}>
          <h2 className="font-heading font-bold uppercase text-[24px] sm:text-[30px] md:text-[36px] lg:text-[48px] text-foreground-950 tracking-tight leading-[0.95] [hyphens:auto] break-words max-w-4xl">
            <span data-line className="block overflow-hidden">
              <span className="block">Ihr komplettes</span>
            </span>
            <span data-line className="block overflow-hidden">
              <span className="block">ENERGIE-SYSTEM</span>
            </span>
          </h2>
        </div>

        {/* Content grid: intro + checklist + image */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_1fr_0.9fr] gap-10 lg:gap-12 items-start mt-10 md:mt-14">
          {/* Intro + CTA */}
          <div className="flex flex-col items-start">
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              Von der Photovoltaik auf dem Dach bis zur Wallbox in der Garage — alle Komponenten geplant und installiert aus einer Hand.
            </p>
            <a
              href="/photovoltaik"
              className="inline-flex items-center mt-8 px-6 py-3 rounded-full bg-accent-500 text-white text-sm font-medium tracking-wider hover:bg-accent-600 transition-colors"
            >
              Unsere Lösungen entdecken
            </a>
          </div>

          {/* Checkmarks */}
          <ul ref={listRef} className="space-y-4 md:space-y-5">
            {intelligentFeatures.map((feature, index) => (
              <li key={index} data-item className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center text-white shrink-0 mt-0.5">
                  <i className="ri-check-line w-3 h-3 flex items-center justify-center text-xs" />
                </span>
                <span className="text-foreground-700 text-sm md:text-base leading-relaxed">{feature}</span>
              </li>
            ))}
          </ul>

          {/* Image */}
          <div ref={imageRef} className="relative overflow-hidden rounded-br-[40px] w-full md:col-span-2 lg:col-span-1">
            <img
              src="https://www.zweering-elektrotechnik.de/wp-content/uploads/2025/11/ODHVuKVLzx5WSXxdKKy.webp"
              alt="Zweering Elektrotechnik Monteur bei Kabelarbeiten an Schaltschrank"
              className="w-full h-64 md:h-[420px] lg:h-full min-h-[320px] object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}